import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Nav from "@/components/Nav";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import { Sparkles, Check, Loader2, XCircle } from "lucide-react";

const POLL_MS = 2500;
const MAX_ATTEMPTS = 20; // ~50s

export default function PremiumSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useAuth();
  const [status, setStatus] = useState("polling"); // polling | success | timeout | error
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;
    let count = 0;
    const tick = async () => {
      if (cancelled) return;
      count += 1;
      setAttempts(count);
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          setStatus("success");
          return;
        }
        if (["failed", "expired"].includes(data.status)) { setStatus("error"); return; }
      } catch (_) {}
      if (count >= MAX_ATTEMPTS) { setStatus("timeout"); return; }
      setTimeout(tick, POLL_MS);
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  if (status === "polling") {
    return (
      <div className="min-h-screen bg-linen">
        <Nav />
        <section className="max-w-lg mx-auto px-5 py-24 text-center" data-testid="premium-polling">
          <div className="w-16 h-16 rounded-full bg-sage/20 border border-sage/40 flex items-center justify-center mx-auto">
            <Loader2 className="w-8 h-8 text-forest animate-spin" />
          </div>
          <h1 className="font-serif text-4xl text-forest mt-6">Confirming your payment…</h1>
          <p className="text-forest/70 mt-3">This usually takes a few seconds. Please don't close this tab.</p>
          <p className="text-xs text-forest/50 mt-4">Attempt {attempts} of {MAX_ATTEMPTS}</p>
        </section>
      </div>
    );
  }

  if (status === "timeout") {
    return (
      <div className="min-h-screen bg-linen">
        <Nav />
        <section className="max-w-lg mx-auto px-5 py-24 text-center">
          <h1 className="font-serif text-4xl text-forest">Still processing…</h1>
          <p className="text-forest/70 mt-3">Stripe is taking a little longer than usual. Refresh in a moment, or check your Membership tab.</p>
          <div className="mt-8 flex gap-3 justify-center">
            <Link to="/dashboard" className="btn-primary">Go to dashboard</Link>
            <button onClick={() => window.location.reload()} className="btn-outline">Retry</button>
          </div>
        </section>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen bg-linen">
        <Nav />
        <section className="max-w-lg mx-auto px-5 py-24 text-center">
          <div className="w-16 h-16 rounded-full bg-taupe flex items-center justify-center mx-auto">
            <XCircle className="w-8 h-8 text-forest" />
          </div>
          <h1 className="font-serif text-4xl text-forest mt-6">Something didn't go through.</h1>
          <p className="text-forest/70 mt-3">Please try again from the pricing page — you weren't charged.</p>
          <Link to="/pricing" className="btn-primary inline-block mt-8">Back to pricing</Link>
        </section>
      </div>
    );
  }

  // success
  return (
    <div className="min-h-screen bg-linen">
      <Seo title="Welcome to Premium" path="/premium/success" noindex />
      <Nav />
      <section className="max-w-2xl mx-auto px-5 sm:px-8 py-24 text-center">
        <div className="w-20 h-20 rounded-full bg-gold/20 border border-gold/40 flex items-center justify-center mx-auto">
          <Sparkles className="w-9 h-9 text-gold" />
        </div>
        <h1 className="font-serif text-5xl text-forest mt-6 leading-tight">Welcome to Premium.</h1>
        <p className="text-forest/70 mt-4 leading-relaxed">Your Sakeenah Circle is fully unlocked — messaging, favourites, bookings and AI matching are ready when you are.</p>
        <div className="grid sm:grid-cols-2 gap-3 mt-10 text-left">
          {["Unlimited caregiver search", "Message caregivers directly", "Save your favourite caregivers", "AI-powered matching"].map((f) => (
            <div key={f} className="card-organic p-4 flex items-center gap-2">
              <Check className="w-5 h-5 text-gold" /> <span className="text-sm text-forest">{f}</span>
            </div>
          ))}
        </div>
        <div className="mt-10 flex justify-center gap-3 flex-wrap">
          <Link to="/browse" className="btn-primary" data-testid="success-browse">Find your caregiver</Link>
          <Link to="/dashboard" className="btn-outline" data-testid="success-dashboard">Go to dashboard</Link>
        </div>
      </section>
    </div>
  );
}
